import { formatBalance, formatAmount } from '@/lib/format-number'
import { resolvePlayerAssetUrl } from '@/lib/player-api'
import { P2P_OPTION_FALLBACK_COLORS } from '@/config/p2p'
import type { P2PMarket, P2PMarketOption, P2PMarketPercent, P2pPercentChartPoint } from '@/types/p2p'

export function parseP2pPercent(percent: string): number {
	const value = Number.parseFloat(percent)

	if (!Number.isFinite(value)) {
		return 0
	}

	return value
}

export function formatP2pPrice(price: string): string {
	const value = Number.parseFloat(price)

	if (!Number.isFinite(value)) {
		return '0'
	}

	return formatBalance(value, 2)
}

export function formatP2pVolume(volume: string): string {
	const value = Number.parseFloat(volume)

	if (!Number.isFinite(value)) {
		return '$ 0 Vol'
	}

	if (value >= 1_000_000) {
		return `$ ${formatAmount(value / 1_000_000)}M Vol`
	}

	if (value >= 1_000) {
		return `$ ${formatAmount(value / 1_000)}K Vol`
	}

	return `$ ${formatAmount(value)} Vol`
}

export function getP2pOptionLogo(option: P2PMarketOption, market: P2PMarket): string {
	return resolvePlayerAssetUrl(option.logo || market.logo)
}

export function getP2pMarketLogo(market: P2PMarket): string {
	return resolvePlayerAssetUrl(market.logo)
}

export function getP2pOptionColor(option: P2PMarketOption, index: number): string {
	if (option.color) {
		return option.color
	}

	return P2P_OPTION_FALLBACK_COLORS[index % P2P_OPTION_FALLBACK_COLORS.length]
}

export function buildP2pPercentChartData(percents: P2PMarketPercent[], options: P2PMarketOption[]): P2pPercentChartPoint[] {
	const sortedPercents = [...percents].sort((left, right) => new Date(left.created_at).getTime() - new Date(right.created_at).getTime())

	return sortedPercents.map((entry) => {
		const createdAt = new Date(entry.created_at)

		const point: P2pPercentChartPoint = {
			timestamp: createdAt.getTime(),
			label: createdAt.toLocaleTimeString('en-US', {
				hour: 'numeric',
				minute: '2-digit',
				hour12: false,
			}),
		}

		for (const option of options) {
			const rawValue = entry.percent[option.name]
			point[option.id] = rawValue === undefined ? 0 : Number(rawValue)
		}

		return point
	})
}

export function getP2pBuyPrice(option: P2PMarketOption, side: 'yes' | 'no'): number {
	const yesPrice = Number.parseFloat(option.price)

	if (!Number.isFinite(yesPrice)) {
		return 0
	}

	return side === 'yes' ? yesPrice : Math.max(0, 1 - yesPrice)
}

export function calculateP2pBuyMetrics(amount: number, price: number) {
	if (!Number.isFinite(amount) || amount <= 0 || !Number.isFinite(price) || price <= 0) {
		return {
			shares: 0,
			potentialReturn: 0,
		}
	}

	const shares = amount / price
	const potentialReturn = shares

	return {
		shares,
		potentialReturn,
	}
}

export function getSelectedP2pOption(market: P2PMarket, selectedOptionId: string | null) {
	return market.options.find((option) => option.id === selectedOptionId) ?? market.options[0] ?? null
}
